import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { fetchGame } from '../../../api/GamesAPI';
import { AuthContext } from '../../AuthContext';
import AppBreadCrumb from '../../../components/common/AppBreadCrumb';
import './ScorecardPage.css';

const ScorecardPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const [game, setGame] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setIsLoading(true);
        fetchGame(id, (response) => {
            setIsLoading(false);
            if (response.game) {
                setGame(response.game);
            } else {
                // Handle error, e.g., redirect to a not-found page
                navigate('/club_portal/games');
            }
        });
    }, [id, navigate]);

    const handleStartScoringClick = () => {
        navigate(`/club_portal/games/${game.id}/score`);
    };

    if (isLoading) {
        return <div className="loading-container"><div className="spinner-border"></div></div>;
    }

    if (!game) {
        return <div className="loading-container">Game not found.</div>;
    }

    const isClubAdmin = user?.roles?.some(role => role.name === 'club-admin');

    // Helper to get team name from team ID
    const getTeamName = (teamId) => game.teams.find(t => t.id === teamId)?.name || 'N/A';

    return (
        <main>
            <div className="container-fluid">
                <AppBreadCrumb title="Game Scorecard" />

                <div className="card mb-4 game-summary-card">
                    <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
                        <h5 className="mb-0 me-3">{game.teams.map(t => t.name).join(' vs ')}</h5>
                        {isClubAdmin && game.status === 'scheduled' && (
                            <div className="mt-2 mt-md-0">
                                <Link to={`/club_portal/games/${game.id}/edit`} className="btn btn-outline-primary btn-sm me-2">
                                    <i className="fas fa-edit me-2"></i>Edit Setup
                                </Link>
                                <button className="btn btn-success btn-sm" onClick={handleStartScoringClick}>
                                    <i className="fas fa-play-circle me-2"></i>Start Live Scoring
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="card-body">
                        <div className="row text-center">
                            <div className="col-md-4"><strong>Venue:</strong> {game.venue.name}</div>
                            <div className="col-md-4"><strong>Date:</strong> {new Date(game.game_date).toLocaleString()}</div>
                            <div className="col-md-4"><strong>Overs:</strong> {game.overs}</div>
                        </div>
                    </div>
                </div>

                {game.innings && game.innings.length > 0 ? game.innings.map(inning => (
                    <div key={inning.id} className="card mb-4">
                        <div className="card-header">
                            <h6>Innings {inning.inning_number}: {getTeamName(inning.batting_team_id)}</h6>
                        </div>
                        <div className="card-body">
                            <h6 className="mb-3">Batting</h6>
                            <div className="table-responsive">
                                <table className="table table-sm scorecard-table">
                                    <thead>
                                        <tr>
                                            <th>Batsman</th>
                                            <th>Dismissal</th>
                                            <th>R</th>
                                            <th>B</th>
                                            <th>4s</th>
                                            <th>6s</th>
                                            <th>SR</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {inning.batting_performances.map(perf => (
                                            <tr key={perf.id}>
                                                <td>{perf.batsman.player_name}</td>
                                                <td>{perf.dismissal_type}</td>
                                                <td>{perf.runs_scored}</td>
                                                <td>{perf.balls_faced}</td>
                                                <td>{perf.fours}</td>
                                                <td>{perf.sixes}</td>
                                                <td>{perf.balls_faced > 0 ? ((perf.runs_scored / perf.balls_faced) * 100).toFixed(2) : '0.00'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <hr />
                            <h6 className="mb-3">Bowling</h6>
                             <div className="table-responsive">
                                <table className="table table-sm scorecard-table">
                                   <thead>
                                        <tr>
                                            <th>Bowler</th>
                                            <th>O</th>
                                            <th>M</th>
                                            <th>R</th>
                                            <th>W</th>
                                            <th>Econ</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                      {inning.bowling_performances.map(perf => (
                                            <tr key={perf.id}>
                                                <td>{perf.bowler.player_name}</td>
                                                <td>{perf.overs_bowled}</td>
                                                <td>{perf.maidens}</td>
                                                <td>{perf.runs_conceded}</td>
                                                <td>{perf.wickets_taken}</td>
                                                <td>{perf.overs_bowled > 0 ? (perf.runs_conceded / perf.overs_bowled).toFixed(2) : '0.00'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )) : (
                    <div className="alert alert-info">Scoring has not started for this game yet.</div>
                )}
            </div>
        </main>
    );
};

export default ScorecardPage;

